import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input.jsx";
import { Label } from "@/components/ui/label.jsx";
import { Skeleton } from "@/components/ui/skeleton.jsx";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useAssistantForm from "@/hooks/forms/useAssistantForm.js";
import { getAssistantImageFromStorage, uploadAssistantImage } from "@/lib";
import { getGradientBackground } from "@/lib/constants/gradients";
import { validateAvatarFile } from "@/lib/db/utils/supabase/storage";
import { supabase } from "@/lib/supabase";
import { useFileStorageStore } from "@/store";
import useAssistantStore from "@/store/assistantStore";
import { AlertTriangle, Camera, Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { InstructionsGeneratorPopup } from "../Chat";
import { ModelSwitcher } from "../Chat/header/ModelSwitcher";

export const getFieldValue = (value, defaultValue) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  return value;
};

/**
 * Validates XML content for unclosed tags
 * @param {string} content - The content to validate
 * @returns {Object} - { isValid: boolean, errors: string[], hasXml: boolean }
 */
const validateXmlTags = (content) => {
  if (!content || typeof content !== "string") {
    return { isValid: true, errors: [], hasXml: false };
  }

  // Check if content contains XML-like tags
  const xmlTagRegex = /<\/?[a-zA-Z][a-zA-Z0-9_-]*[^>]*>/g;
  const xmlTags = content.match(xmlTagRegex);

  if (!xmlTags || xmlTags.length === 0) {
    return { isValid: true, errors: [], hasXml: false };
  }

  const errors = [];
  const tagStack = [];
  const selfClosingTags = new Set([
    "br",
    "hr",
    "img",
    "input",
    "meta",
    "link",
    "area",
    "base",
    "col",
    "embed",
    "source",
    "track",
    "wbr",
  ]);

  for (const tag of xmlTags) {
    // Remove < and > and any attributes to get just the tag name
    const tagContent = tag.slice(1, -1).trim();

    // Check if it's a self-closing tag (ends with /)
    if (tagContent.endsWith("/")) {
      continue; // Self-closing tags are valid
    }

    // Check if it's a closing tag
    if (tagContent.startsWith("/")) {
      const tagName = tagContent.slice(1).split(/\s+/)[0].toLowerCase();

      if (tagStack.length === 0) {
        errors.push(`Closing tag </${tagName}> has no matching opening tag`);
        continue;
      }

      const lastOpenTag = tagStack.pop();
      if (lastOpenTag !== tagName) {
        errors.push(
          `Mismatched tags: expected </${lastOpenTag}> but found </${tagName}>`
        );
      }
    } else {
      // Opening tag
      const tagName = tagContent.split(/\s+/)[0].toLowerCase();

      // Skip self-closing HTML tags
      if (!selfClosingTags.has(tagName)) {
        tagStack.push(tagName);
      }
    }
  }

  // Check for unclosed tags
  while (tagStack.length > 0) {
    const unclosedTag = tagStack.pop();
    errors.push(`Unclosed tag: <${unclosedTag}>`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    hasXml: true,
  };
};

export const AssistantForm = ({
  assistant,
  onSavingStateChange,
  streamingFields,
  isStreaming,
}) => {
  const {
    // Form setup
    errors,
    isSaving,
    formValues,
    tools,

    // UI state
    isGeneratingInstructions,
    hasUnsavedChanges,

    // Handlers
    handleFieldUpdate,
    handleInstructionsGenerated,
    saveAllChanges,

    // Dialog controls
    setIsEditingInstructions,
    setIsGeneratingInstructions,
    setHasUnsavedChanges,
  } = useAssistantForm(assistant);

  const { updateAssistantImage, setCurrentAssistant, currentAssistant } =
    useAssistantStore();
  const { assistantImages } = useFileStorageStore();
  const [imageFile, setImageFile] = useState(null);
  const [imageSrc, setImageSrc] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const instructionsRef = useRef(null);
  const prevIsStreamingRef = useRef(isStreaming);

  const [showSaveSuccess, setShowSaveSuccess] = useState(false);

  // Add state for XML validation
  const [xmlValidation, setXmlValidation] = useState({
    isValid: true,
    errors: [],
    hasXml: false,
  });

  // Add effect to show success indicator after saving
  useEffect(() => {
    if (isSaving) {
      setShowSaveSuccess(false);
    } else {
      // Show success check mark briefly after saving completes
      if (formValues.name) {
        // Only if form has content to avoid initial load
        setShowSaveSuccess(true);
        const timer = setTimeout(() => {
          setShowSaveSuccess(false);
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [isSaving, formValues.name]);

  // Add XML validation effect
  useEffect(() => {
    if (formValues.instructions) {
      const validation = validateXmlTags(formValues.instructions);
      setXmlValidation(validation);
    } else {
      setXmlValidation({ isValid: true, errors: [], hasXml: false });
    }
  }, [formValues.instructions]);

  // Send saving state to parent component
  useEffect(() => {
    if (onSavingStateChange) {
      onSavingStateChange(
        isSaving,
        showSaveSuccess,
        hasUnsavedChanges,
        saveAllChanges
      );
    }
  }, [
    isSaving,
    showSaveSuccess,
    hasUnsavedChanges,
    saveAllChanges,
    onSavingStateChange,
  ]);

  // Update form values when streaming fields change
  useEffect(() => {
    if (streamingFields && isStreaming) {
      // Update form fields with streaming values
      if (streamingFields.name && streamingFields.name !== formValues.name) {
        handleFieldUpdate("name", streamingFields.name);
      }
      if (
        streamingFields.description !== undefined &&
        streamingFields.description !== formValues.description
      ) {
        handleFieldUpdate("description", streamingFields.description);
      }
      if (
        streamingFields.instructions !== undefined &&
        streamingFields.instructions !== formValues.instructions
      ) {
        handleFieldUpdate("instructions", streamingFields.instructions);
      }
    }
  }, [streamingFields, isStreaming, formValues, handleFieldUpdate]);

  // Auto-scroll instructions textarea to bottom while streaming
  useEffect(() => {
    if (isStreaming && instructionsRef.current && streamingFields?.instructions) {
      const textarea = instructionsRef.current;
      textarea.scrollTop = textarea.scrollHeight;
    }
  }, [isStreaming, streamingFields?.instructions]);

  // Reset unsaved changes when streaming ends (data is saved by parent component)
  useEffect(() => {
    // Detect transition from streaming to not streaming
    if (prevIsStreamingRef.current && !isStreaming) {
      // Streaming just ended - the parent component has already saved the data
      setHasUnsavedChanges(false);
    }
    prevIsStreamingRef.current = isStreaming;
  }, [isStreaming, setHasUnsavedChanges]);

  const refreshAssistantImage = useCallback(async () => {
    if (!assistant?.image_path) return;

    try {
      // Get a fresh signed URL for the image
      const freshImageUrl = await getAssistantImageFromStorage(
        assistant.image_path
      );

      if (freshImageUrl) {
        setImageSrc(freshImageUrl);
      }
    } catch (error) {
      console.error("Error refreshing assistant image:", error);
      // Fall back to using the stored URLs if refresh fails
      const fallbackUrl = assistant.image_url || assistant.avatar_url;
      if (fallbackUrl) {
        setImageSrc(fallbackUrl);
      }
    }
  }, [assistant?.image_path]);

  useEffect(() => {
    if (assistant) {
      // Always try to refresh the image URL from the storage path first
      if (assistant.image_path) {
        refreshAssistantImage();
      } else {
        // If no image path, fall back to the stored URLs
        const imageUrl = assistant.image_url || assistant.avatar_url || "";

        if (imageUrl) {
          setImageSrc(imageUrl);
        } else if (assistantImages && assistant.image_path) {
          // Last resort: check assistantImages from the store
          const assistantImage = assistantImages.find(
            (image) => image.path === assistant.image_path
          );

          if (assistantImage?.base64) {
            setImageSrc(assistantImage.base64);
          }
        }
      }
    } else {
      // Reset image if no assistant
      setImageSrc("");
      setImageFile(null);
    }
  }, [assistant, assistantImages, refreshAssistantImage]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const { isValid, error: validationError } = validateAvatarFile(file);
    if (!isValid) {
      console.warn("Invalid avatar file:", validationError);
      return;
    }

    setImageFile(file);

    const localPreview = URL.createObjectURL(file);
    setImageSrc(localPreview);
  };

  const handleImageUpdate = useCallback(
    async (imageFile) => {
      if (!imageFile || !assistant) {
        console.warn("No image file or assistant provided");
        return;
      }

      setIsUploading(true);

      try {
        // Upload the image to storage
        const filePath = await uploadAssistantImage(assistant, imageFile);

        // Get a fresh signed URL
        const imageUrl = await getAssistantImageFromStorage(filePath);

        // Determine the best ID to use
        let assistantId;
        let supabaseId;

        // Find Supabase ID (if assistant is from OpenAI)
        if (assistant.id && !assistant.id.startsWith("asst_")) {
          supabaseId = assistant.id;
          assistantId = assistant.id;
        } else if (assistant.metadata?.id) {
          supabaseId = assistant.metadata.id;
          assistantId = assistant.metadata.id;
        } else {
          // Fallback to assistant ID
          assistantId = assistant.id || assistant.assistant_id;
          // Try to find Supabase ID in database
          try {
            const { data, error } = await supabase
              .from("assistants")
              .select("id")
              .eq("id", assistantId)
              .single();

            if (!error && data && data.id) {
              supabaseId = data.id;
            }
          } catch (lookupError) {
            console.error("Error looking up Supabase ID:", lookupError);
          }
        }

        // Create image update data
        const imageUpdateData = {
          avatar_url: imageUrl,
          image_path: filePath,
          image_url: imageUrl,
          // Include metadata update to ensure Supabase sync happens
          metadata: {
            ...(assistant.metadata || {}),
            image_path: filePath,
            avatar_url: imageUrl,
            image_url: imageUrl,
            updated_at: new Date().toISOString(),
          },
        };

        // TRY BOTH UPDATE APPROACHES:

        // 1. Update through the store first
        let storeUpdateSuccessful = false;
        try {
          // Update both the storage path and the URLs in the database via store
          await updateAssistantImage(assistantId, imageUpdateData);
          storeUpdateSuccessful = true;
        } catch (storeError) {
          console.error("Store update failed for image:", storeError);
          // Continue to try direct update
        }

        // 2. DIRECT SUPABASE UPDATE - as a failsafe mechanism
        let directUpdateSuccessful = false;
        if (supabaseId) {
          try {
            // Create a Supabase-friendly update object
            const supabaseUpdateData = {
              avatar_url: imageUrl,
              image_path: filePath,
              image_url: imageUrl,
              updated_at: new Date().toISOString(),
              metadata: {
                ...(assistant.metadata || {}),
                image_path: filePath,
                avatar_url: imageUrl,
                image_url: imageUrl,
                updated_at: new Date().toISOString(),
              },
            };

            const { data: updatedRecord, error } = await supabase
              .from("assistants")
              .update(supabaseUpdateData)
              .eq("id", supabaseId)
              .select()
              .single();

            if (error) {
              console.error("Direct Supabase image update error:", error);
            } else {
              directUpdateSuccessful = true;
            }
          } catch (directError) {
            console.error(
              "Exception during direct Supabase image update:",
              directError
            );
          }
        } else if (assistantId && assistantId.startsWith("asst_")) {
          // If no Supabase ID but we have OpenAI ID, create a new record
          try {
            const supabaseInsertData = {
              name: assistant.name || "Unnamed Assistant",
              description: assistant.description || "",
              instructions: assistant.instructions || "",
              model: assistant.model || "gpt-4.1-mini",
              avatar_url: imageUrl,
              image_path: filePath,
              image_url: imageUrl,
              user_id: assistant.user_id || assistant.metadata?.user_id,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              metadata: {
                ...(assistant.metadata || {}),
                image_path: filePath,
                avatar_url: imageUrl,
                image_url: imageUrl,
                updated_at: new Date().toISOString(),
              },
            };

            const { data: newAssistant, error } = await supabase
              .from("assistants")
              .insert(supabaseInsertData)
              .select()
              .single();

            if (error) {
              console.error(
                "Error creating new assistant record for image:",
                error
              );
            } else {
              supabaseId = newAssistant.id;
              directUpdateSuccessful = true;

              // Note: OpenAI assistant metadata updates are no longer needed
              // as we now manage assistants entirely through Supabase
              console.log("Assistant image updated successfully in Supabase");
            }
          } catch (createError) {
            console.error("Error creating new record for image:", createError);
          }
        }

        // Show appropriate notification based on results
        if (storeUpdateSuccessful || directUpdateSuccessful) {
          // Update local state with the fresh URL
          setImageSrc(imageUrl);

          // Update the currentAssistant in the store
          setCurrentAssistant({
            ...currentAssistant,
            avatar_url: imageUrl,
            image_path: filePath,
            image_url: imageUrl,
            metadata: {
              ...(currentAssistant?.metadata || {}),
              image_path: filePath,
              avatar_url: imageUrl,
              image_url: imageUrl,
              updated_at: new Date().toISOString(),
              ...(supabaseId
                ? { id: supabaseId, supabase_id: supabaseId }
                : {}),
            },
          });
        } else {
          // Both update methods failed
          console.warn(
            "Image was uploaded but database update failed. Changes may not persist."
          );
        }

        return imageUrl;
      } catch (error) {
        console.error("Error updating assistant image:", error);
        return null;
      } finally {
        setIsUploading(false);
      }
    },
    [assistant, updateAssistantImage, currentAssistant, setCurrentAssistant]
  );

  useEffect(() => {
    const uploadImageToSupabase = async () => {
      if (imageFile && assistant && !isUploading) {
        const result = await handleImageUpdate(imageFile);
        if (result) {
          setImageFile(null);
        }
      }
    };

    uploadImageToSupabase();
  }, [imageFile, handleImageUpdate, assistant, isUploading]);

  // Add function to handle instructions blur (removed save functionality)
  const handleInstructionsBlur = () => {
    // Instructions textarea blurred
  };

  // Handle instructions focus
  const handleInstructionsFocus = () => {
    setIsEditingInstructions(true);
  };

  return (
    <div className="space-y-4 min-h-full">
      {/* Name Section with Avatar on Right */}
      <div className="space-y-0">
        <div className="py-1">
          <div className="flex items-start gap-3">
            {/* Avatar Column */}
            <div
              className="relative group cursor-pointer flex-shrink-0 flex items-center justify-center self-center"
              onClick={handleAvatarClick}
            >
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={handleFileChange}
                disabled={isUploading}
              />
              <Avatar className="h-12 w-12 cursor-pointer transition-all group-hover:opacity-80">
                {isUploading ? (
                  <div className="flex items-center justify-center w-full h-full bg-muted">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                ) : (
                  <>
                    <AvatarImage
                      src={imageSrc}
                      alt={assistant?.name || "Assistant"}
                      onError={(e) => {
                        console.error("Failed to load avatar image:", e);
                        e.currentTarget.src = "";
                      }}
                    />
                    <AvatarFallback
                      className={`text-white ${getGradientBackground(assistant)}`}
                    >
                      {assistant?.name?.charAt(0)?.toUpperCase() || "A"}
                    </AvatarFallback>
                  </>
                )}
              </Avatar>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Camera className="h-4 w-4 text-white" />
              </div>
            </div>

            {/* Name Label and Input Column */}
            <div className="flex-1 flex flex-col">
              <Label
                htmlFor="name"
                className="text-[13px] text-foreground font-medium mb-1.5 block"
              >
                Assistant Name
              </Label>
              <Input
                id="name"
                value={formValues.name || ""}
                onChange={(e) => handleFieldUpdate("name", e.target.value)}
                placeholder="Enter assistant name"
                className="h-8 border-gray-600 focus:border-blue-500 bg-transparent text-foreground rounded-2xl text-[13px]"
              />
              {errors.name && (
                <p className="text-[11px] text-red-400 mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="py-1">
          <Label
            htmlFor="description"
            className="text-[13px] text-foreground font-medium mb-1.5 block"
          >
            Description
          </Label>
          <Input
            id="description"
            value={formValues.description || ""}
            onChange={(e) => handleFieldUpdate("description", e.target.value)}
            placeholder="What does this assistant do?"
            className="h-8 border-gray-600 focus:border-blue-500 bg-transparent text-foreground rounded-2xl text-[13px]"
          />
          {errors.description && (
            <p className="text-[11px] text-red-400 mt-1">
              {errors.description.message}
            </p>
          )}
        </div>

        {/* Instructions */}
        <div className="py-1">
          <div className="flex items-center gap-2 mb-1.5">
            <Label
              htmlFor="instructions"
              className="text-[13px] text-foreground font-medium"
            >
              System Instructions
            </Label>
            <div
              style={{
                position: "relative",
                zIndex: 100,
                display: "inline-block",
              }}
            >
              <InstructionsGeneratorPopup
                type="instructions"
                onGenerated={(instructions) => {
                  handleInstructionsGenerated(instructions);
                }}
                isLoading={isGeneratingInstructions}
                setIsLoading={setIsGeneratingInstructions}
                className="h-5 w-5 border-blue-500 text-blue-500 hover:bg-blue-500/10 hover:border-blue-600 bg-transparent rounded-full p-0 flex items-center justify-center"
                toolNames={tools.map((tool) =>
                  tool.type === "function" ? tool.function.name : tool.type
                )}
              />
            </div>
            {/* Streaming Indicator */}
            {isStreaming && (
              <div className="flex items-center gap-1 text-purple-500">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span className="text-[11px] font-medium">Generating...</span>
              </div>
            )}
            {/* XML Validation Indicator */}
            {xmlValidation.hasXml && !xmlValidation.isValid && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="text-red-400 hover:text-red-300 cursor-help">
                      <AlertTriangle className="h-4 w-4" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-xs bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800"
                  >
                    <div className="space-y-1">
                      <p className="font-medium text-sm text-red-900 dark:text-red-100">
                        XML Validation Errors:
                      </p>
                      {xmlValidation.errors.map((error, index) => (
                        <p
                          key={index}
                          className="text-xs text-red-700 dark:text-red-200"
                        >
                          • {error}
                        </p>
                      ))}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>

          {isGeneratingInstructions ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-[90%]" />
              <Skeleton className="h-4 w-[80%]" />
              <Skeleton className="h-4 w-[85%]" />
              <Skeleton className="h-4 w-[75%]" />
            </div>
          ) : (
            <>
              <Textarea
                ref={instructionsRef}
                id="instructions"
                className={`min-h-[70px] border-gray-600 focus:border-blue-500 bg-transparent text-foreground rounded-2xl text-[13px] ${
                  xmlValidation.hasXml && !xmlValidation.isValid
                    ? "border-red-400 focus:border-red-500"
                    : ""
                }`}
                value={formValues.instructions || ""}
                onChange={(e) =>
                  handleFieldUpdate("instructions", e.target.value)
                }
                onFocus={handleInstructionsFocus}
                onBlur={handleInstructionsBlur}
                placeholder="How should this assistant behave? What are its capabilities and limitations?"
              />
              {errors.instructions && (
                <p className="text-[11px] text-red-400 mt-1">
                  {errors.instructions.message}
                </p>
              )}
              {/* XML Validation Error Messages */}
              {xmlValidation.hasXml && !xmlValidation.isValid && (
                <div className="mt-2 p-2 bg-red-900/20 border border-red-800 rounded-2xl">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-3 w-3 text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-[11px] font-medium text-red-300 mb-1">
                        XML Tag Validation Errors:
                      </p>
                      <ul className="text-[10px] text-red-400 space-y-0.5">
                        {xmlValidation.errors.map((error, index) => (
                          <li key={index} className="list-disc list-inside">
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Model Selection */}
        <div className="flex items-center justify-between py-1">
          <div className="flex-1 pr-4">
            <Label className="text-[13px] text-foreground font-medium block">
              AI Model
            </Label>
          </div>
          <div className="flex-shrink-0">
            <ModelSwitcher zIndex={10000} />
          </div>
        </div>
      </div>
    </div>
  );
};

AssistantForm.propTypes = {
  assistant: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    model: PropTypes.string,
    instructions: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.object,
    file_ids: PropTypes.arrayOf(PropTypes.string),
    files: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        filename: PropTypes.string,
        name: PropTypes.string,
        purpose: PropTypes.string,
        description: PropTypes.string,
      })
    ),
    tools: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string, // Tool name (unformatted)
        PropTypes.shape({
          type: PropTypes.string.isRequired,
          function: PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            parameters: PropTypes.object,
          }),
        }),
      ])
    ),
    tool_resources: PropTypes.shape({
      file_search: PropTypes.shape({
        vector_store_ids: PropTypes.arrayOf(PropTypes.string),
      }),
      code_interpreter: PropTypes.shape({
        file_ids: PropTypes.arrayOf(PropTypes.string),
      }),
    }),
    temperature: PropTypes.number,
    top_p: PropTypes.number,
  }),
  onSavingStateChange: PropTypes.func,
  streamingFields: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    instructions: PropTypes.string,
  }),
  isStreaming: PropTypes.bool,
};

export default AssistantForm;
